import {observe, action, observable, autorun, computed, runInAction, reaction} from 'mobx';
import {autobind, nonenumerable} from 'spa/decorators';
import moment from 'moment';

import Cookies from 'js-cookie';

const GENDERS = ['male', 'female'];
const SORT_OPTIONS = [
  '', // WARNING - Our default sort value! Relevant Search by default.
  'rating',
  'traits.soothe',
  'traits.technical',
  'traits.deep',
  'traits.pamper',
];

const PREGNANCY_ID = 4;
const OSTEO_FOLLOW_UP_ID = 231;

const SINGLE_BOOKING_TYPE = 'single';
const BACK_2_BACK_BOOKING_TYPE = 'back2back';
const COUPLES_BOOKING_TYPE = 'couples';

class SelectionState {
  get DATE_FORMAT() { return 'Y-MM-DD'; }

  // Campaign related
  @observable campaign_id = undefined;
  @observable promocode = undefined;

  @observable therapists = 1;
  @observable duration = 1;
  @observable duration2 = 1;

  @observable selectedCategory = undefined;
  @observable selectedCategoryInfo;

  @observable selectedVertical = undefined;
  @observable verticalConfirmed = false;

  @observable categoryHasOnlyOneBookingType = false;

  @observable basketConfirmed = false;
  @observable basketValidated = false;

  @observable addTreatmentActive = false;
  @observable addTreatmentCategoryStep = false;

  @observable bookingType = 'single';
  @observable bookingTypeConfirmed = false;
  @observable durationConfirmed = false;
  @observable duration2Confirmed = false;
  @observable hasChangedType = false;
  @observable hasChangedDuration = false;
  @observable hasChangedDuration2 = false;
  @observable selectSameTreatmentBoth = 'true';

  @observable place_id = undefined;
  @observable address_id = undefined;
  @observable city_id = undefined;
  @observable addressConfirmed = false;
  @observable treatment = undefined;
  @observable treatmentConfirmed = false;
  @observable treatment2 = undefined;
  @observable treatment2Confirmed = false;
  @observable therapist = [];
  @observable filteredTreatments = [];
  @observable filteredTreatmentsMatrix = [];
  @observable therapistConfirmed = false;
  @observable date = undefined;
  @observable endDate = undefined;
  @observable slot_id = undefined;
  @observable slotConfirmed = false;
  @observable offer_popup = undefined;

  @observable therapistInfoViewing = undefined;
  @observable treatmentInfoViewing = undefined;
  @observable therapistFirst = false;
  @observable gender = '';
  @observable sort = SORT_OPTIONS[0];
  @observable therapistFilterText = '';

  @observable basketItems = [];
  @observable basketWorkerCount = 1;

  getQuery(prevQuery, override) {
    const query = {
      ...prevQuery,
      ...this,
      ...override,
    };

    delete query.now;

    Object.keys(query)
      .forEach((key) => {
        switch (key) { // eslint-disable-line default-case
        case 'addressConfirmed':
        case 'treatmentConfirmed':
        case 'treatment2Confirmed':
        case 'verticalConfirmed':
        case 'therapistConfirmed':
        case 'slotConfirmed':
        case 'hasChangedType':
        case 'hasChangedDuration':
        case 'hasChangedDuration2':
        case 'durationConfirmed':
        case 'duration2Confirmed':
        case 'therapistFirst':
          if (query[key]) {
            query[key] = null;
          } else {
            delete query[key];
          }
          break;
        case 'basketItems':
          // basketItems in the url has the following structure:
          // 3170%2B3171%2C1%2B1
          //
          // which is the equivalent to:
          // 3170+3171,1+1
          //
          // This translates into the following in the basket state:
          //
          // [
          //   {
          //     speciality_id: 3170,
          //     duration_id: 3171,
          //   },
          //   {
          //     speciality_id: 1,
          //     duration_id: 1,
          //   }
          // ]
          if (query[key] && query[key].length) {
            let tempQuery = '';

            query[key].forEach((item) => {
              tempQuery += Object.keys(item).map(itemKey => item[itemKey]).join('+');
              tempQuery += ',';
            });

            tempQuery = tempQuery.slice(0, -1);

            query[key] = tempQuery;
          } else {
            delete query[key];
          }
          break;
        case 'therapist':
        case 'filteredTreatments':
          if (query[key] && query[key].length) {
            query[key] = query[key].join(',');
          } else {
            delete query[key];
          }
          break;
        case 'therapistFilterText':
          if (!query[key] || query[key] === '') {
            delete query[key];
          }
          break;
        case 'date':
        case 'endDate':
        case 'place_id':
        case 'address_id':
        case 'city_id':
        case 'campaign_id':
        case 'promocode':
        case 'offer_popup':
        case 'therapistInfoViewing':
        case 'treatmentInfoViewing':
          if (query[key]) {
            query[key] += '';
          } else {
            delete query[key];
          }
          break;
        case 'gender':
          if (GENDERS.indexOf(query[key]) === -1) {
            delete query[key];
          }
          break;
        case 'sort':
          if (SORT_OPTIONS[0] === query[key] || SORT_OPTIONS.indexOf(query[key]) === -1) {
            delete query[key];
          }
          break;
        case 'cachedQuery':
          if (query[key]) {
            delete query[key];
          }
          break;
        /* NOTE Remove the following once the new site is fully live */
        case 'hands':
        case 'product':
        case 'slot':
        case 'speciality':
        case 'worker':
        case 'term':
          if (query[key]) {
            delete query[key];
          }
          break;
        }
      });

    if (query.people < 2 || query.therapists > 1) delete query.duration2;
    return query;
  }

  @action loadQuery(query) {
    /* eslint-disable no-param-reassign */
    /*
      NOTE handling old booking flow query params these can be removed once we move fully to the
      new site
    */
    let hands = 1;
    if (query.hands) {
      hands = parseInt(query.hands, 10) / 2 || 1;
      query.therapists = hands % 2 === 0 ? hands : 1;
    }

    if (query.product) {
      query.duration = query.product;
      if (hands > 1) {
        query.duration2 = query.product;
      }
    }

    if (query.speciality) {
      query.treatment = query.speciality;
      query.treatmentConfirmed = true;

      if (hands > 1) {
        query.treatment2 = query.speciality;
        query.treatment2Confirmed = true;
      }
    }

    if (moment.unix(query.date).isValid()) {
      query.date = moment.unix(query.date).format(this.DATE_FORMAT);
    }

    if (query.slot) {
      query.slot_id = query.slot;
      query.slotConfirmed = true;
    }

    if (query.worker) {
      query.therapist = query.worker;
      query.therapistConfirmed = true;
    }
    /* eslint-enable no-param-reassign */

    for (const key of Object.keys(this)) {
      let value = this[key];
      switch (key) {
      case 'basketWorkerCount':
      case 'therapists':
      case 'people':
      case 'duration':
      case 'duration2':
      case 'selectSameTreatmentBoth':
      case 'treatment':
      case 'treatment2':
      case 'slot_id':
      case 'therapistInfoViewing':
      case 'treatmentInfoViewing':
        if (query[key] && isFinite(query[key]) && !isNaN(query[key])) { // eslint-disable-line no-restricted-globals
          value = parseInt(query[key], 10);
        }
        break;
      case 'addressConfirmed':
      case 'treatmentConfirmed':
      case 'treatment2Confirmed':
      case 'verticalConfirmed':
      case 'therapistConfirmed':
      case 'slotConfirmed':
      case 'hasChangedType':
      case 'hasChangedDuration':
      case 'hasChangedDuration2':
      case 'durationConfirmed':
      case 'duration2Confirmed':
      case 'therapistFirst':
        value = query[key] !== undefined;
        break;
      case 'basketItems': {
        // basketItems in the url has the following structure:
        // 3170%2B3171%2C1%2B1
        //
        // which is the equivalent to:
        // 3170+3171,1+1
        //
        // This translates into the following in the basket state:
        //
        // [
        //   {
        //     speciality_id: 3170,
        //     duration_id: 3171,
        //   },
        //   {
        //     speciality_id: 1,
        //     duration_id: 1,
        //   }
        // ]
        let tempBasketItems = '';

        if (this[key] && this[key].length) {
          this[key].forEach((item) => {
            tempBasketItems += Object.keys(item).map(itemKey => item[itemKey]).join('+');
            tempBasketItems += ',';
          });
        }

        if (query[key] && (query[key] !== tempBasketItems)) {
          value = (decodeURIComponent(query[key]) || '')
            .split(',')
            .map((item) => {
              const listItems = item.split('+');

              const final = {};

              final.speciality_id = parseInt(listItems[0], 10);

              final.duration_id = parseInt(listItems[1], 10);

              return final;
            });
        }
        break;
      }
      case 'therapist':
      case 'filteredTreatments':
        if ((query[key] || '') !== this[key].join(',')) {
          value = (decodeURIComponent(query[key]) || '')
            .split(',')
            .map(parseFloat)
            .filter(Boolean);
        }
        break;
      case 'therapistFilterText':
        if (!query[key] || query[key] === '') {
          value = '';
        }
        break;
      case 'gender':
        if (GENDERS.indexOf(query[key]) === -1) {
          value = '';
        } else {
          value = query[key];
        }
        break;
      case 'sort':
        if (SORT_OPTIONS.indexOf(query[key]) === -1) {
          [value] = SORT_OPTIONS;
        } else {
          value = query[key];
        }
        break;
      case 'isToday':
      case 'now':
      case 'dateNow':
      case 'hourNow':
      case 'city_id':
      case 'filteredTreatmentsMatrix':
        continue; //eslint-disable-line no-continue
      default:
        value = query[key] ? `${query[key]}` : undefined;
        break;
      }

      if (value !== this[key]) {
        this[key] = value;
      }
    }
  }

  @nonenumerable
  @observable
  now = moment().toJSON();

  @computed get isToday() {
    return this.date === this.dateNow;
  }
  @computed get dateNow() {
    return moment(this.now).format(this.DATE_FORMAT);
  }
  @computed get hourNow() {
    return moment(this.now).hour();
  }

  @computed get genderOptions() {
    const {i18n} = this.ctx;
    return [{
      label: i18n.__('All Genders'),
      value: '',
    }, {
      label: i18n.__('Male'),
      value: 'male',
    }, {
      label: i18n.__('Female'),
      value: 'female',
    }];
  }

  @computed get genderOptionsConcise() {
    const {i18n} = this.ctx;
    return [{
      label: i18n.__('All'),
      value: '',
    }, {
      label: i18n.__('Male'),
      value: 'male',
    }, {
      label: i18n.__('Female'),
      value: 'female',
    }];
  }

  /**
   * Generate our date options for a dropdown list, i.e. today tomorrow, sat, sun. Also takes
   * into account next Saturday and next Sunday for the correct purposes.
   *
   * @returns {Array} Array of date options {value, label} for the date select options
   */
  @computed get dateSelectOptions() {
    const {i18n} = this.ctx;
    const dayOfWeek = moment().day();

    const dates = [{
      label: i18n.__('Today', 'Therapist directory date selection dropdown'),
      value: moment(),
    }, {
      label: i18n.__('Tomorrow', 'Therapist directory date selection dropdown'),
      value: moment().add(1, 'days'),
    }];

    dayOfWeek < 5 &&
      dates.push({
        value: moment().day(6),
        label: i18n.__('This Saturday', 'Therapist directory date selection dropdown'),
      });

    dayOfWeek < 6 &&
      dates.push({
        value: moment().day(7),
        label: i18n.__('This Sunday', 'Therapist directory date selection dropdown'),
      });

    // calculate the next seven days range
    const nextSeven = {
      value: `${moment().format(this.DATE_FORMAT)}_${moment().add(7, 'days').format(this.DATE_FORMAT)}`,
      label: i18n._x('Next 7 Days', 'Therapist directory date selection dropdown'),
    };

    // calculate the next thirty days range
    const nextThirty = {
      value: `${moment().format(this.DATE_FORMAT)}_${moment().add(30, 'days').format(this.DATE_FORMAT)}`,
      label: i18n._x('Next 30 Days', 'Therapist directory date selection dropdown'),
    };

    // If the current day is Saturday or Sunday then we can calculate the next Saturday and Sunday
    if (dayOfWeek === 6 || dayOfWeek === 7) {
      const nextSat = {
        value: moment().day(6).add(1, 'weeks'),
        label: i18n.__('Next Saturday', 'Therapist directory date selection dropdown'),
      };

      const nextSun = {
        value: moment().day(7).add(1, 'weeks'),
        label: i18n.__('Next Sunday', 'Therapist directory date selection dropdown'),
      };

      dates.push(nextSat, nextSun);
    }

    // Add our nextSeven and nextThirty options to the end of our list
    dates.push(nextSeven, nextThirty);

    /* Return with the dates correctly formatted. Note that our date ranges are stings here and
     * and shouldn't be formatted again.
     */
    return dates.map(date => (
      {
        label: date.label,
        value: typeof date.value === 'string' ? date.value : date.value.format(this.DATE_FORMAT),
      }
    ));
  }

  /**
   * Sets our selected gender to the value of our gender selection box
   *
   * @param {Object} event - The React synthetic event
   */
  @autobind @action onSelectGender(event) {
    this.gender = event.target.value;
  }

  /**
   * Sets our selected date to the value of our date selection dropdown
   *
   * @param {Object} event - The React synthetic event
   */
  @autobind @action onSelectDate(event) {
    const date = event.target.value;
    if (date && typeof date === 'string' && date.indexOf('_') !== -1) {
      const dateSplit = date.split('_');
      [this.date, this.endDate] = dateSplit;
    } else {
      this.date = event.target.value;
      this.endDate = undefined;
    }
  }

  @computed get sortOptions() {
    const {i18n} = this.ctx;
    return [{
      label: i18n._x('Most Relevant', 'Therapist filters'),
      value: '',
    }, {
      label: i18n._x('Overall Rating', 'Therapist filters'),
      value: 'rating',
    }, {
      label: i18n._x('Most Soothing', 'Therapist filters'),
      value: 'traits.sooth',
    }, {
      label: i18n._x('Most Technical', 'Therapist filters'),
      value: 'traits.technical',
    }, {
      label: i18n._x('Most Deep', 'Therapist filters'),
      value: 'traits.deep',
    }, {
      label: i18n._x('Most Pampering', 'Therapist filters'),
      value: 'traits.pamper',
    }];
  }

  @autobind @action onSelectSort(event) {
    this.sort = event.target.value;
  }

  @computed get isTreatmentPregnancy() {
    return this.treatment === PREGNANCY_ID;
  }

  @computed get isTreatment2Pregnancy() {
    return this.treatment2 === PREGNANCY_ID;
  }

  @computed get isTreatmentOsteoFollowUp() {
    return this.treatment === OSTEO_FOLLOW_UP_ID;
  }

  @computed get singleBookingType() {
    return SINGLE_BOOKING_TYPE;
  }

  @computed get couplesBookingType() {
    return COUPLES_BOOKING_TYPE;
  }

  @computed get back2backBookingType() {
    return BACK_2_BACK_BOOKING_TYPE;
  }

  @computed get isActiveTreatmentPregnancy() {
    if (this.treatmentConfirmed) {
      return !this.treatment2Confirmed && this.isTreatment2Pregnancy;
    }
    return this.isTreatmentPregnancy;
  }

  @computed get isActiveTreatmentOsteoFollowUp() {
    return this.isTreatmentOsteoFollowUp;
  }

  @computed get isFiltered() {
    return this.gender || this.sort !== SORT_OPTIONS[0];
  }

  @computed get isFilteredTreatments() {
    return !!this.filteredTreatments.length;
  }

  @computed get isSearchApplied() {
    return this.therapistFilterText !== '';
  }

  @autobind @action clearFilters() {
    [this.sort] = SORT_OPTIONS;
    this.gender = '';
    this.filteredTreatments = [];
    this.filteredTreatmentsMatrix = [];
  }

  @autobind @action clearSearch() {
    this.therapistFilterText = '';
  }

  @nonenumerable
  listeners = [
    reaction(
      () => this.promocode,
      () => {
        if (this.promocode && !Cookies.get('promocode')) {
          Cookies.set('promocode', this.promocode);
        }
      },
    ),
  ];

  @action reset() {
    // Campaign related
    this.campaign_id = undefined;
    this.promocode = undefined;

    this.address_id = undefined;
    this.addressConfirmed = false;
    this.basketConfirmed = false;
    this.basketItems = [];
    this.basketWorkerCount = 1;
    this.basketValidated = false;
    this.bookingType = 'single';
    this.date = undefined;
    this.endDate = undefined;
    this.gender = '';
    this.place_id = undefined;
    this.selectedVertical = undefined;
    this.verticalConfirmed = false;
    this.slot_id = undefined;
    this.slotConfirmed = false;
    this.therapist = [];
    this.therapistConfirmed = false;
    this.therapistFirst = false;
    this.therapistInfoViewing = undefined;
    this.treatmentInfoViewing = undefined;
    this.treatment = undefined;
    this.treatmentConfirmed = false;
    this.treatment2 = undefined;
    this.treatment2Confirmed = false;

    [this.sort] = SORT_OPTIONS;
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners.push(clearInterval.bind(
      null,
      setInterval(action('heartbeat', () => {
        this.now = moment().toJSON();
      }), 60000),
    ));
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default SelectionState;



// WEBPACK FOOTER //
// ./spa/state/selection.jsx